import React, { useEffect, useState } from "react";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../../firebaseConfig";
import classes from "./Settings.module.css";

const tabs = ["Profile", "Privacy", "Notifications", "Appearance", "Account"];

const Settings = ({ user }) => {
  const [activeTab, setActiveTab] = useState("Profile");
  const [profile, setProfile] = useState({});
  const [edited, setEdited] = useState({});
  const [locked, setLocked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");

  useEffect(() => {
    if (!user) return;
    const loadProfile = async () => {
      const ref = doc(db, "users", user.uid);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        setProfile(snap.data());
        setEdited(snap.data());
      }
      setLoading(false);
    };
    loadProfile();
  }, [user]);

  // Reset lock when switching tabs
  const handleTabSwitch = (tab) => {
    setActiveTab(tab);
    setLocked(false);
    setStatus("");
    setEdited(profile);
  };

  const handleChange = (field, value) => {
    if (locked) return;
    setEdited((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!user) return;
    setStatus("Saving...");
    try {
      const ref = doc(db, "users", user.uid);
      await updateDoc(ref, edited);
      setProfile(edited);
      setStatus("✅ Saved successfully!");
      setLocked(true);
    } catch (err) {
      console.error(err);
      setStatus("❌ Error saving changes.");
    } finally {
      setTimeout(() => setStatus(""), 2500);
    }
  };

  const renderSwitch = (field, label) => (
    <div className={classes.row}>
      <span>{label}</span>
      <label className={classes.switch}>
        <input
          type="checkbox"
          checked={edited[field] ?? false}
          onChange={(e) => handleChange(field, e.target.checked)}
          disabled={locked}
        />
        <span className={classes.slider}></span>
      </label>
    </div>
  );

  const renderProfile = () => (
    <div>
      <h3>Profile</h3>
      <label className={classes.label}>Username</label>
      <input
        className={classes.input}
        value={edited.username || ""}
        onChange={(e) => handleChange("username", e.target.value)}
        disabled={locked}
      />
      <label className={classes.label}>Bio</label>
      <textarea
        className={classes.textarea}
        value={edited.bio || ""}
        onChange={(e) => handleChange("bio", e.target.value)}
        disabled={locked}
      />
      <label className={classes.label}>Email</label>
      <input className={classes.input} value={user?.email || ""} disabled />
    </div>
  );

  const renderPrivacy = () => (
    <div>
      <h3>Privacy</h3>
      {renderSwitch("showProfile", "Show Profile Publicly")}
      {renderSwitch("allowSharing", "Allow Mindmap Sharing")}
      <div className={classes.row}>
        <span>Data Visibility</span>
        <input type="text" className={classes.input} value="Private" disabled />
      </div>
    </div>
  );

  const renderNotifications = () => (
    <div>
      <h3>Notifications</h3>
      {renderSwitch("emailNotifications", "Email Notifications")}
      {renderSwitch("weeklySummary", "Weekly Summary")}
      {renderSwitch("communityMentions", "Community Mentions")}
    </div>
  );

  const renderAppearance = () => (
    <div>
      <h3>Appearance</h3>
      {renderSwitch("darkMode", "Dark Mode")}
      {renderSwitch("compactLayout", "Compact Layout")}
    </div>
  );

  const renderAccount = () => (
    <div>
      <h3>Account Info</h3>
      <p><strong>UID:</strong> {user?.uid}</p>
      <p><strong>Email:</strong> {user?.email}</p>
      <p>
        <strong>Date Joined:</strong>{" "}
        {profile?.createdAt?.toDate?.().toLocaleDateString() || "Unknown"}
      </p>
      <p>This section is read-only for account integrity.</p>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case "Profile": return renderProfile();
      case "Privacy": return renderPrivacy();
      case "Notifications": return renderNotifications();
      case "Appearance": return renderAppearance();
      case "Account": return renderAccount();
      default: return null;
    }
  };

  if (loading) return <p>Loading...</p>;

  return (
    <div className={classes.container}>
      <aside className={classes.sidebar}>
        <h2>Settings</h2>
        <ul className={classes.menu}>
          {tabs.map((t) => (
            <li
              key={t}
              className={`${classes.menuItem} ${
                activeTab === t ? classes.active : ""
              }`}
              onClick={() => handleTabSwitch(t)}
            >
              {t}
            </li>
          ))}
        </ul>
      </aside>

      <main className={classes.content}>
        <div className={classes.panel}>
          {renderContent()}
          {activeTab !== "Account" && (
            <div className={classes.saveWrapper}>
              <button
                className={classes.saveBtn}
                onClick={handleSave}
                disabled={locked}
              >
                {locked ? "Saved (Locked)" : "💾 Save Changes"}
              </button>
            </div>
          )}
        </div>

        {status && <p className={classes.status}>{status}</p>}
      </main>
    </div>
  );
};

export default Settings;
